import { AscBadgeColor, ignoreAttribute } from "@ascendnow/shared";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { CheckMarkIcon } from "../../AscIcons";
import styles from "./AscMilestoneProgressBar.module.scss";

export interface AscMilestoneProgressBarProps {
  value: number;
  max: number;
  fullWidth?: boolean;
  vertical?: boolean;
  dotted?: boolean;
  className?: string;
  options?: {
    link?: string;
    isActive?: boolean;
  }[];
  onClick?: (link: string) => void;
  variant?: "Admin" | "Platform";
  color?: AscBadgeColor;
}

export default function AscMilestoneProgressBar({
  value = 0,
  max = 100,
  fullWidth = false,
  vertical,
  dotted = false,
  options,
  className,
  variant = "Platform",
  onClick,
  color,
}: AscMilestoneProgressBarProps) {
  const [currentStep, setCurrentStep] = useState<number>();

  useEffect(() => {
    if (value > max) {
      setCurrentStep(100);
      return;
    }
    if (value <= 0) {
      setCurrentStep(0);
      return;
    }
    const percentage = ((value - 1) * 100) / (max - 1 || 1);

    setCurrentStep(percentage);
  }, [value, max]);

  const steps = useMemo(() => {
    const _steps = [];

    for (let i = 1; i <= max; i++) {
      const isActive = options?.[i - 1]?.isActive;
      _steps.push(
        <AscProgressStep
          key={i}
          variant={variant}
          href={options?.[i - 1]?.link}
          active={isActive === undefined ? i <= value : isActive}
          onClick={onClick}
        />
      );
    }

    return _steps;
  }, [max, options, variant, value, onClick]);

  return (
    <div
      className={classNames(
        styles.ground,
        {
          [styles.fullWidth]: fullWidth,
          [styles.vertical]: vertical,
          [styles.dotted]: dotted,
          [styles.teal]: color === "TEAL",
          [styles.pink]: color === "PINK",
          [styles.purple]: color === "PURPLE",
          [styles.green]: color === "GREEN",
          [styles.orange]: color === "ORANGE",
        },
        className
      )}
      {...ignoreAttribute.contrast}
    >
      <div
        className={classNames(
          styles.progressBarContainer,
          variant === "Admin" && styles.admProgressBarContainer
        )}
        {...ignoreAttribute.contrast}
      >
        <div className={styles.stepsContainer} {...ignoreAttribute.contrast}>
          {steps}
        </div>
        <div
          style={
            vertical
              ? { height: `${currentStep}%` }
              : { width: `${currentStep}%` }
          }
          className={classNames(
            styles.progressBar,
            variant === "Admin" && styles.admProgressBar
          )}
          {...ignoreAttribute.contrast}
        />
      </div>
    </div>
  );
}

interface AscProgressStepProps {
  active: boolean;
  href?: string;
  variant?: "Admin" | "Platform";
  onClick?: (link: string) => void;
}
function AscProgressStep({
  active,
  href,
  variant,
  onClick,
}: AscProgressStepProps) {
  function handleOnClick() {
    if (!href) {
      return;
    }
    onClick?.(href);
  }

  return (
    <span
      onClick={handleOnClick}
      className={classNames(
        styles.progressStep,
        variant === "Admin" && styles.admProgressStep,
        {
          [styles.active]: active,
        }
      )}
      {...ignoreAttribute.contrast}
    >
      <CheckMarkIcon
        className={styles.icon}
        {...ignoreAttribute.contrast}
      />
    </span>
  );
}
